import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../auth/Auth";
import ErrorResponse from "../ErrorResponse";
import { Col, Container, Modal, Row } from "react-bootstrap";
import LoadingIndicator from "../LoadingIndicator";
import useSortableData from "../useSortableData";
import { useVirtualizer, defaultRangeExtractor } from "@tanstack/react-virtual";
import ListenersHeader from "./ListenersHeader";
import ListenerRow from "./ListenersRow";
import ListenerHistoryModal from "./ListenerHistoryModal";

export default function ListenersModal({ show, onHide, radioId, startDatetime, endDatetime }) {
    const [loading, setLoading] = useState(true);
    const [errorCode, setErrorCode] = useState(null);
    const [data, setData] = useState(null);
    const [sessionData, setSessionData] = useState({});
    const [selectedListener, setSelectedListener] = useState(null);

    const tableContainerRef = useRef(null);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const input = {
                    radio_id: radioId,
                    start_datetime: startDatetime,
                    end_datetime: endDatetime,
                };
                setSessionData({});
                const response = await axiosInstance.post(`listeners/`, input);
                setErrorCode(null);
                setData(response.data);
            } catch (error) {
                setData(null);
                if (error?.code !== "ERR_CANCELED") setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        }
        if (show && radioId && startDatetime && endDatetime) fetchData();
    }, [show, endDatetime, radioId, startDatetime]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(data?.listeners, { key: "start_datetime", direction: "ascending" });

    const fetchSessionData = useCallback(
        async index => {
            if (data && !sessionData[index]) {
                setSessionData(prev => ({ ...prev, [index]: "loading" }));
                const item = sortedItems[index];
                const input = {
                    area_id: data.area_id,
                    start_datetime: item.start_datetime,
                    end_datetime: item.end_datetime,
                };
                const response = await axiosInstance.post(`listeners/${item.listener_id}`, input);
                setSessionData(prev => ({ ...prev, [index]: response.data }));
            }
        },
        [data, sessionData, sortedItems]
    );

    const virtualizer = useVirtualizer({
        count: sortedItems?.length,
        estimateSize: () => 48,
        overscan: 10,
        getScrollElement: () => tableContainerRef.current,
        rangeExtractor: useCallback(
            range => {
                const indexes = defaultRangeExtractor(range);
                indexes.forEach(index => {
                    fetchSessionData(index);
                });
                return indexes;
            },
            [fetchSessionData]
        ),
    });

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable dialogClassName="modal-xxxl">
            <Modal.Header closeButton className=" py-1 py-xl-3">
                <Modal.Title className=" text-uppercase">Listeners</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-0 pb-1" ref={tableContainerRef}>
                {!loading && errorCode && <ErrorResponse errorCode={errorCode} />}
                {loading && (
                    <span colSpan={12}>
                        <LoadingIndicator />
                    </span>
                )}
                {!loading && data && (
                    <>
                        <ListenersHeader sortConfig={sortConfig} requestSort={requestSort} />
                        <div>
                            <Container fluid className="position-relative" style={{ height: virtualizer.getTotalSize() }}>
                                {virtualizer.getVirtualItems().map(virtualRow => {
                                    const item = sortedItems[virtualRow.index];
                                    return (
                                        <ListenerRow
                                            key={virtualRow.key}
                                            item={item}
                                            virtualRow={virtualRow}
                                            virtualizer={virtualizer}
                                            sessionData={sessionData[virtualRow.index]}
                                            setSelectedListener={setSelectedListener}
                                        />
                                    );
                                })}
                                {!virtualizer.getVirtualItems().length && (
                                    <Row className="py-1 border-bottom">
                                        <Col className=" text-center">No Listeners</Col>
                                    </Row>
                                )}
                            </Container>
                        </div>
                        <ListenerHistoryModal
                            show={!!selectedListener}
                            onHide={() => setSelectedListener(false)}
                            areaId={data.area_id}
                            listener={selectedListener}
                            endDatetime={endDatetime}
                        />
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}
